import React from 'react';
import { OrangeCircleTick } from '../../assets/svg';
import ReactHtmlParser from 'html-react-parser';

interface Offer {
  offer_badge_string: string;
  offer_substring: string;
  offer_ends_string: string;
}

interface Plan {
  after_billing_end_string: string;
  after_billing_end_string_without_hours: string;
  billing_period: string;
  price_monthly: string;
  checkout_page_offer_string: string;
  crossed_price: string;
  group_classes_access: boolean;
  hourly_price_string: string;
  currency_symbol: string;
  is_best_value: boolean;
  is_free_month_offer: number;
  is_lto: number;
  offer: Offer;
  plan_name: string;
  plan_type: string;
  prepay_discount: string;
  price: string;
  price_without_currency: string;
  recordings_access: boolean;
  save_string: string;
  session_cost: string;
  sessions_per_four_weeks: number;
  sessions_per_week: number;
  personalised_pricing_data: {
    crossed_price: string;
    price: string;
    save_string: string;
  };
}

interface PlanTableMobileProps {
  planData: Plan[];
}

// formats the hourly price string into a React node like "$16.75" + "/hr" or returns 'N/A'
const formatHourlyPrice = (
  hourlyPriceString?: string,
  currencySymbol?: string
): React.ReactNode => {
  const getPrice = hourlyPriceString ? String(hourlyPriceString).trim() : '';
  if (!getPrice) return 'N/A';

  const match = getPrice.match(/^(\\D*\d[\d,\.\s]*)/);
  const price = match ? match[1].trim() : getPrice.replace(/\/.*/, '').trim();

  // If no numeric digit present, show 'N/A'
  if (!/\d/.test(price)) return 'N/A';

  return (
    <>
      {currencySymbol}
      {price}
      <span className="text-[#9B9B9B] text-[11.5px] font-[550]">/hr</span>
    </>
  );
};

const PlanTableMobile: React.FC<PlanTableMobileProps> = ({ planData }) => {
  if (!planData || planData.length === 0) return null;

  const TableRow = ({
    label,
    value,
    isCost = false
  }: {
    label: string;
    value: React.ReactNode;
    isCost?: boolean;
  }) => (
    <tr className="border-b border-border">
      <td className="px-[25px] py-[18px] border-x-0 text-left">
        <p className="text-[12.5px] font-[550] leading-[18px] text-[#555] w-full max-w-[180px] sm:max-w-full">
          {label}
        </p>
      </td>
      <td className="pr-[25px] border-x-0 text-end">
        {isCost ? (
          <div className="text-[13px] font-[550] leading-[14px]">{value}</div>
        ) : (
          <div
            className={`${typeof value === 'number' ? 'bg-[#ececec] w-[24px] h-[20px]' : ''} inline-flex items-center justify-center bg-gray-light rounded text-[13px] font-[550] leading-[20px]`}
          >
            {value}
          </div>
        )}
      </td>
    </tr>
  );

  return (
    <>
      {planData.map((plan, index) => (
        <section
          id="plan-table-mobile"
          key={index}
          className={`w-full min-w-[320px] mx-auto ${plan.is_best_value ? 'bg-[#FFD4B51A]' : 'bg-white'} rounded-xl shadow-sm border border-border overflow-hidden mb-6`}
        >
          {/* Header */}
          <div className="px-6 pt-6 pb-4">
            <h2 className="font-inter text-[15px] font-semibold leading-[normal] text-[#ee731b]">
              {plan.plan_name}
            </h2>
          </div>

          {/* Table */}
          <div>
            <table className="font-monaSans w-full">
              <tbody>
                <TableRow
                  label="Number of 1-on-1 Sessions per week"
                  value={plan.sessions_per_week ? plan.sessions_per_week : 'N/A'}
                />
                <TableRow
                  label="Number of 1-on-1 Sessions per 4 weeks"
                  value={plan.sessions_per_four_weeks ? plan.sessions_per_four_weeks : 'N/A'}
                />
                <TableRow
                  label="1-on-1 Session Cost"
                  value={formatHourlyPrice(plan.hourly_price_string, plan.currency_symbol)}
                  isCost
                />
                <TableRow
                  label="Access to 50+ daily live group classes"
                  value={<OrangeCircleTick className="w-[20px] h-[20px]" />}
                />
                <TableRow
                  label="Access to recordings"
                  value={<OrangeCircleTick className="w-[20px] h-[20px]" />}
                />
                <TableRow label="Billing period" value={plan.billing_period} />
                <TableRow
                  label="Monthly Base price"
                  value={
                    <span>
                      {plan.price_monthly}
                      <span className="text-[#9B9B9B]">/mo</span>
                    </span>
                  }
                />
              </tbody>
            </table>
          </div>

          {/* Pricing Section */}
          <div className={`p-[25px] ${plan.is_best_value ? '' : 'bg-[#fbfbfb]'}`}>
            <div className="flex items-center gap-[6px] mb-[8px]">
              <span className="text-[24px] font-bold leading-[normal]">
                {plan.personalised_pricing_data.price}
              </span>
              {plan.personalised_pricing_data.crossed_price && (
                <span className="text-[15px] font-bold leading-[normal] text-[#BFBFBF] line-through">
                  {plan.personalised_pricing_data.crossed_price}
                </span>
              )}
            </div>

            {plan.personalised_pricing_data.save_string && (
              <div className="text-[11.5px] font-semibold leading-[16px] mb-[5px]">
                <span className="text-[#636363]">
                  {ReactHtmlParser(plan.personalised_pricing_data.save_string)}
                </span>
              </div>
            )}

            <div className="text-gray-medium">
              <p className="text-[11.5px] font-medium leading-[16px] text-[#969696] price-card">
                {ReactHtmlParser(plan.after_billing_end_string_without_hours)}
              </p>
            </div>
          </div>
        </section>
      ))}
    </>
  );
};

export default PlanTableMobile;
